import ApiService from '@/service/ApiService';
import { usePopupStore } from '@/store/alertpopup-store';
import { useAuthStore } from '@/store/auth-store';
import { useLoadingStore } from '@/store/loading-store';
import { isRef, ref, unref, watchEffect } from 'vue';

const defaultConfig = {
    method: 'get'
};

const defaultOptions = {
    immediate: true,
    silent: false
};

export const useAxios = (url, config = {}, options = {}, router) => {
    const response = ref(null);
    const data = ref([]);
    const error = ref(null);
    const loading = useLoadingStore();
    const popupStore = usePopupStore();
    const authStore = useAuthStore();
    const toastComponentRef = ref(null);

    const showToast = (severity, summary, detail, life = 600) => {
        if (toastComponentRef.value) {
            toastComponentRef.value.showToast(severity, summary, detail, life);
        }
    };
    const { onSuccess, onError, immediate, silent } = {
        ...defaultOptions,
        ...options
    };

    let { params } = config;
    params = params ? Object.fromEntries(Object.entries(params).map(([key, value]) => [key, Array.isArray(value) ? value.join(',') : value])) : {};

    const setHeader = (key, value) => {
        ApiService.setHeader(key, value);
    };

    const removeHeader = (key) => {
        ApiService.removeHeader(key);
    };

    const handleError = (err) => {
        let errorMessage = '알 수 없는 오류가 발생했습니다.';
        let errorCode = 'UNKNOWN_ERROR';
        let popupmsg = false;
        const response = {
            data: {
                data: null
            }
        };

        if (err.response) {
            const { status, data } = err.response;
            errorCode = data?.code || `ERROR_${status}`;
            errorMessage = data?.msg || errorMessage;
            if (data && data.data) {
                response.data.data = data.data;
            }

            switch (status) {
                case 400:
                    //errorMessage = '잘못된 요청입니다.';
                    errorCode = 'BAD_REQUEST';
                    break;
                case 401:
                    //errorMessage = '인증 오류입니다. 로그인 또는 권한이 불충분합니다.';
                    errorCode = 'UNAUTHORIZED';
                    popupmsg = true;
                    break;
                case 403:
                    //errorMessage = '접근 권한이 없습니다.';
                    errorCode = 'FORBIDDEN';
                    popupmsg = true;
                    break;
                case 409:
                    //errorMessage = err.response.data || '접근 권한이 없습니다.';
                    errorCode = 'CONFLICT'; //err.response.data.msg || 'CONFLICT';
                    break;
                case 415:
                    //errorMessage = 'Content-Type이 올바르지 않습니다.';
                    errorCode = 'UNSUPPORTED_MEDIA_TYPE';
                    break;
                case 500:
                    //errorMessage = '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
                    errorCode = 'SERVER_ERROR';
                    popupmsg = true;
                    break;
                default:
                    errorMessage = `서버 오류 (${status})가 발생했습니다.`;
                    errorCode = 'SERVER_ERROR';
            }
        } else if (err.code === 'ERR_NETWORK') {
            errorMessage = '네트워크 오류가 발생했습니다.';
            errorCode = 'NETWORK_ERROR';
            popupmsg = true;
        } else if (err.isContentTypeError) {
            errorMessage = 'Content-Type이 올바르지 않습니다.';
            errorCode = 'UNSUPPORTED_MEDIA_TYPE';
        }

        console.error(`[${errorCode}] ${errorMessage}`);
        //showToast('error', errorCode, errorMessage);
        if (popupmsg) {
            popupStore.showPopup(errorCode, errorMessage);
        }

        const errorPayload = response.data && response.data.data != null ? response.data : response;

        return { errorCode, errorMessage, response: response, originalError: err, error: errorPayload };
    };

    const execute = (body) => {
        return new Promise((resolve, reject) => {
            data.value = [];
            error.value = null;
            const finalMethod = config.method || 'get'; // method가 없으면 기본값 'get'

            if (!silent) {
                loading.showLoading();
            }

            //  Content-Type 설정 (multipart 요청에따른 contenttype 설정)
            let headers = {};
            if (body instanceof FormData) {
                headers['Content-Type'] = 'multipart/form-data';
            } else {
                headers['Content-Type'] = 'application/json';
            }

            ApiService.request(finalMethod, unref(url), body, unref(params), headers)
                .then((res) => {
                    response.value = res;
                    data.value = res.data.data;
                    if (onSuccess) {
                        onSuccess(res);
                    }
                    resolve(res);
                })
                .catch(async (err) => {
                    const handledError = handleError(err);
                    error.value = handledError;

                    if (handledError.errorCode === 'FORBIDDEN') {
                        await authStore.logout();
                    }

                    if (onError) {
                        onError(handledError);
                    }
                    reject(handledError);
                })
                .finally(() => {
                    if (!silent) {
                        loading.hideLoading();
                    }
                    // removeHeader('Content-Type');
                    // setHeader('Content-Type', 'application/json');
                });
        });
    };

    if ((immediate && isRef(params)) || isRef(url)) {
        watchEffect(() => {
            const resolvedUrl = unref(url);
            if (resolvedUrl && resolvedUrl !== '') {
                if (immediate) {
                    execute();
                }
            }
        });
    } else {
        if (immediate) {
            execute();
        }
    }
    return {
        response,
        data,
        error,
        loading: ref(loading.loading),
        setHeader,
        removeHeader,
        execute
    };
};
